import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FaUser,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaShieldAlt,
  FaCheckCircle,
} from "react-icons/fa";
import { updateUserBasicProfile } from "../../State/User/Action";
import { useMyContext } from "../../store/ContextApi";
import "tailwindcss/tailwind.css";

const BasicProfile = () => {
  const [role, setRole] = useState("ROLE_LEARNER");
  const [showSuccess, setShowSuccess] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    isInstructor,
    isLearner,
    isAdmin,
    setIsInstructor,
    setIsLearner,
    setIsAdmin,
  } = useMyContext();

  const {
    updateUserBasicProfileResult,
    updateUserBasicProfileLoading,
    updateUserBasicProfileError,
  } = useSelector((store) => store.user);

  const handleSubmit = () => {
    dispatch(updateUserBasicProfile({ role }));
  };

  useEffect(() => {
    if (isInstructor || isLearner || isAdmin) {
      navigate("/home");
    }
  }, [isInstructor, isLearner, isAdmin, navigate]);

  useEffect(() => {
    if (updateUserBasicProfileResult?.data) {
      toast.success("Profile updated successfully!");
      setShowSuccess(true);

      // Reset all roles
      setIsInstructor(false);
      setIsLearner(false);
      setIsAdmin(false);

      // Set correct role
      if (role === "ROLE_INSTRUCTOR") {
        setIsInstructor(true);
        // Navigate after short delay
        setTimeout(() => {
          navigate("/home");
        }, 1500);
      } else if (role === "ROLE_LEARNER") {
        setIsLearner(true);
        // Navigate learners to browse-courses
        setTimeout(() => {
          navigate("/browse-courses");
        }, 1500);
      }
    }
    if (updateUserBasicProfileError) {
      toast.error(
        updateUserBasicProfileError.message ||
          "An error occurred while updating your profile."
      );
    }
  }, [
    updateUserBasicProfileResult,
    updateUserBasicProfileError,
    role,
    setIsInstructor,
    setIsLearner,
    setIsAdmin,
    navigate,
  ]);

  // Choose icon based on role
  const getRoleIcon = () => {
    if (role === "ROLE_INSTRUCTOR")
      return <FaChalkboardTeacher className="text-gray-600" />;
    if (role === "ROLE_ADMIN") return <FaShieldAlt className="text-gray-600" />;
    return <FaUserGraduate className="text-gray-600" />;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-500 p-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-2xl p-10 w-full max-w-lg relative"
      >
        <h2 className="text-3xl font-bold mb-6 text-center flex items-center justify-center gap-2">
          <FaUser className="text-purple-600" /> Your Basic Profile
        </h2>

        <div className="space-y-5">
          {/* Role selection */}
          <div className="flex items-center gap-3">
            {getRoleIcon()}
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="flex-1 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
            {/* <option value="ROLE_INSTRUCTOR">Instructor</option> */}
              <option value="ROLE_LEARNER">Learner</option>
            </select>
          </div>

          {/* Submit button */}
          <motion.button
            whileHover={{ scale: !updateUserBasicProfileLoading ? 1.05 : 1 }}
            whileTap={{ scale: !updateUserBasicProfileLoading ? 0.95 : 1 }}
            onClick={handleSubmit}
            disabled={updateUserBasicProfileLoading}
            className={`w-full py-3 rounded-lg mt-6 font-semibold shadow-md transition-all flex items-center justify-center gap-2
              ${
                updateUserBasicProfileLoading
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-purple-600 text-white hover:bg-purple-700"
              }`}
          >
            {updateUserBasicProfileLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Submitting...
              </>
            ) : showSuccess ? (
              <>
                <FaCheckCircle className="text-green-400" />
                Success!
              </>
            ) : (
              "Submit"
            )}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default BasicProfile;
